import { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CAMPAIGN_ID } from '@constants/common';
import { useCampaignTypes } from '@hooks/api/useCampaignTypes';
import { useUser } from '@hooks/api/useUser';
import { useGetSearchParam } from '@hooks/common/useGetSearchParams';
import ScraperSettings from '@pages/LeadScraper/components/ScraperSettings';
import ScrapperSummary from '@pages/LeadScraper/components/ScrapperSummary';
import { ScraperProps } from '@pages/LeadScraper/types';
import { Spinner } from '@components/theme-v2';

import { LeadScraperProvider } from './context';

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: var(--sk-bg-primary);
`;

const MainContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: var(--sk-space-4);
`;

const LeadScraper: FC<ScraperProps<string>> = ({
  title,
  scraperSubtitleLocale,
  submitButtonLocale,
  breadCrumbsLocale,
  editable = false,
}) => {
  const { t } = useTranslation();
  const { isLoading: isUserLoading } = useUser();
  const { isLoading: isCampaignTypesLoading } = useCampaignTypes();
  const campaignId: string | null = useGetSearchParam(CAMPAIGN_ID);

  const isLoading = [isUserLoading, isCampaignTypesLoading].some(Boolean);

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner size="lg" />
      </LoadingContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t(title)}</title>
      </Helmet>
      <LeadScraperProvider
        campaignId={campaignId}
        scraperSubtitleLocale={scraperSubtitleLocale}
        submitButtonLocale={submitButtonLocale}
        breadCrumbsLocale={breadCrumbsLocale}
        editable={editable}
      >
        <MainContainer>
          <ScraperSettings />
          <ScrapperSummary />
        </MainContainer>
      </LeadScraperProvider>
    </>
  );
};

export default memo(LeadScraper);
